import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { BarcodeStabilizer, type BarcodeStabilizerOptions } from "./stabilizer";
import { sampleVideoBrightness, LowLightDetector, type LowLightDetectorOptions } from "./lightDetector";
import {
  createNativeBarcodeDetector,
  cropVideoToViewfinder,
  detectFromNative,
  normalizeDetectedValue,
} from "./barcodeDetect";

// MVP-supported symbologies (spec §15).
const SUPPORTED_FORMATS = [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E];
const DEFAULT_STABILIZER_OPTIONS: Partial<BarcodeStabilizerOptions> = {
  requiredHits: 2,
  detectionWindowMs: 1200,
  cooldownMs: 2000,
};

export interface UseBarcodeScannerOptions {
  /** Camera + decode loop only runs while enabled (gated on auth + scanner state, spec: no lookups without a valid session). */
  enabled: boolean;
  onDetected: (barcode: string) => void;
  /** Optional low light detector configuration. */
  lowLightOptions?: LowLightDetectorOptions;
  /** Optional barcode stabilizer configuration (default cooldown is 2s debounce on same barcode). */
  stabilizerOptions?: Partial<BarcodeStabilizerOptions>;
}

export type CameraErrorType = "permission_denied" | "not_found" | "unknown";

export interface CameraError {
  message: string;
  type: CameraErrorType;
}

export interface ZoomCapabilities {
  min: number;
  max: number;
  step: number;
}

export interface CameraDevice {
  deviceId: string;
  label: string;
  type: "main" | "wide" | "macro" | "telephoto" | "other";
}

export interface UseBarcodeScannerResult {
  error: CameraError | null;
  hasTorch: boolean;
  isTorchOn: boolean;
  isLowLight: boolean;
  toggleTorch: () => Promise<void>;
  enableTorch: () => Promise<void>;
  // Zoom support
  zoom: number;
  minZoom: number;
  maxZoom: number;
  stepZoom: number;
  hasZoom: boolean;
  setZoom: (zoom: number) => Promise<void>;
  resetZoom: (preset?: number) => Promise<void>;
  // Multi-camera support
  cameras: CameraDevice[];
  selectedCameraId: string | null;
  currentCameraLabel: string;
  hasMultipleCameras: boolean;
  switchCamera: (deviceId?: string) => Promise<void>;
  cycleCamera: () => Promise<void>;
  restartCamera: () => void;
}

/** Extracts the active video MediaStreamTrack from an HTMLVideoElement. */
export function getActiveTrack(videoElement: HTMLVideoElement | null | undefined): MediaStreamTrack | null {
  if (!videoElement?.srcObject) return null;
  const src = videoElement.srcObject;
  if ("getVideoTracks" in src && typeof src.getVideoTracks === "function") {
    const tracks = src.getVideoTracks();
    if (tracks.length > 0) return tracks[0];
  }
  return null;
}

/** Checks whether a MediaStreamTrack has torch capability. Safely degrades if unsupported. */
export function isTorchSupported(track: MediaStreamTrack | null | undefined): boolean {
  if (!track) return false;
  try {
    if (typeof track.getCapabilities === "function") {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
      return Boolean(capabilities && "torch" in capabilities && capabilities.torch);
    }
  } catch {
    // Safe degradation if getCapabilities throws or is unsupported
  }
  return false;
}

/** Checks if torch is available from controls or the active video stream track. */
export function checkTorchAvailability(
  controls: IScannerControls | null,
  videoElement: HTMLVideoElement | null
): boolean {
  if (controls && typeof controls.switchTorch === "function") {
    return true;
  }
  const track = getActiveTrack(videoElement);
  if (track && isTorchSupported(track)) {
    return true;
  }
  return false;
}

/** Sets the torch state on the video track or scanner controls. */
export async function setTorchState(
  enabled: boolean,
  controls: IScannerControls | null,
  videoElement: HTMLVideoElement | null
): Promise<void> {
  const track = getActiveTrack(videoElement);
  if (track && isTorchSupported(track) && typeof track.applyConstraints === "function") {
    await track.applyConstraints({
      advanced: [{ torch: enabled } as MediaTrackConstraintSet & { torch?: boolean }],
    });
    return;
  }
  if (controls && typeof controls.switchTorch === "function") {
    await controls.switchTorch(enabled);
    return;
  }
}

/** Extracts zoom capabilities from a MediaStreamTrack if supported. */
export function getZoomCapabilities(track: MediaStreamTrack | null | undefined): ZoomCapabilities | null {
  if (!track) return null;
  try {
    if (typeof track.getCapabilities === "function") {
      const caps = track.getCapabilities() as MediaTrackCapabilities & {
        zoom?: { min: number; max: number; step?: number };
      };
      if (caps && caps.zoom && typeof caps.zoom.min === "number" && typeof caps.zoom.max === "number") {
        if (caps.zoom.max > caps.zoom.min) {
          return {
            min: caps.zoom.min,
            max: caps.zoom.max,
            step: caps.zoom.step || 0.1,
          };
        }
      }
    }
  } catch {
    // Safe degradation
  }
  return null;
}

/** Retrieves the current zoom setting from a MediaStreamTrack. */
export function getTrackZoom(track: MediaStreamTrack | null | undefined): number | null {
  if (!track) return null;
  try {
    if (typeof track.getSettings === "function") {
      const settings = track.getSettings() as MediaTrackSettings & { zoom?: number };
      if (typeof settings.zoom === "number") {
        return settings.zoom;
      }
    }
  } catch {
    // Safe degradation
  }
  return null;
}

/** Applies zoom constraints to a MediaStreamTrack. */
export async function applyTrackZoom(
  track: MediaStreamTrack | null | undefined,
  zoom: number
): Promise<void> {
  if (!track || typeof track.applyConstraints !== "function") return;
  const caps = getZoomCapabilities(track);
  let clampedZoom = zoom;
  if (caps) {
    clampedZoom = Math.min(Math.max(zoom, caps.min), caps.max);
  }
  await track.applyConstraints({
    advanced: [{ zoom: clampedZoom } as MediaTrackConstraintSet & { zoom?: number }],
  });
}

/** Checks whether a video device represents a rear/environment camera. */
export function isBackCamera(device: MediaDeviceInfo): boolean {
  const lower = (device.label || "").toLowerCase();
  if (
    lower.includes("front") ||
    lower.includes("user") ||
    lower.includes("selfie") ||
    lower.includes("face")
  ) {
    return false;
  }
  return true;
}

/** Classifies a camera device into Main / Ultra-wide / Macro / Telephoto. */
export function classifyCameraDevice(
  device: MediaDeviceInfo,
  index: number,
  total: number
): CameraDevice {
  const rawLabel = device.label || "";
  const lower = rawLabel.toLowerCase();

  let type: CameraDevice["type"] = "other";
  let label = rawLabel || `Камера ${index + 1}`;

  if (lower.includes("macro")) {
    type = "macro";
    label = "Macro";
  } else if (
    lower.includes("ultra") ||
    lower.includes("wide-angle") ||
    lower.includes("0.5x") ||
    lower.includes("0.6x")
  ) {
    type = "wide";
    label = "Ultra-wide";
  } else if (
    lower.includes("telephoto") ||
    lower.includes("tele") ||
    lower.includes("zoom") ||
    lower.includes("3x") ||
    lower.includes("5x")
  ) {
    type = "telephoto";
    label = "Telephoto";
  } else if (
    lower.includes("main") ||
    lower.includes("primary") ||
    lower.includes("standard") ||
    lower.includes("triple") ||
    lower.includes("dual") ||
    (lower.includes("back") && !lower.includes("2") && !lower.includes("3")) ||
    index === 0
  ) {
    type = "main";
    label = "Main";
  } else if (index === 1 && total >= 2) {
    type = "wide";
    label = "Ultra-wide";
  } else if (index === 2 && total >= 3) {
    type = "macro";
    label = "Macro";
  }

  return {
    deviceId: device.deviceId,
    label,
    type,
  };
}

/**
 * Runs the camera through @zxing/browser's continuous decode loop (spec §14:
 * scanning happens on-device, camera frames never leave it) and confirms a
 * barcode via BarcodeStabilizer before calling back — one confirmed event per
 * stable read, not one per frame (spec §16-17).
 */
export function useBarcodeScanner(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  opts: UseBarcodeScannerOptions
): UseBarcodeScannerResult {
  const [error, setError] = useState<CameraError | null>(null);
  const [hasTorch, setHasTorch] = useState<boolean>(false);
  const [isTorchOn, setIsTorchOn] = useState<boolean>(false);
  const [isLowLight, setIsLowLight] = useState<boolean>(false);

  // Zoom state
  const [zoom, setZoomState] = useState<number>(1);
  const [minZoom, setMinZoom] = useState<number>(1);
  const [maxZoom, setMaxZoom] = useState<number>(5);
  const [stepZoom, setStepZoom] = useState<number>(0.1);
  const [hasZoom, setHasZoom] = useState<boolean>(false);

  // Camera devices state
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);

  const controlsRef = useRef<IScannerControls | null>(null);
  const stabilizerRef = useRef(new BarcodeStabilizer({ ...DEFAULT_STABILIZER_OPTIONS, ...opts.stabilizerOptions }));
  const lightDetectorRef = useRef(new LowLightDetector(opts.lowLightOptions));
  const onDetectedRef = useRef(opts.onDetected);
  onDetectedRef.current = opts.onDetected;
  const preferredDeviceIdRef = useRef<string | null>(null);
  const [cameraGeneration, setCameraGeneration] = useState(0);

  const refreshCameras = useCallback(async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        const backCameras = videoDevices.filter(isBackCamera);
        const targetList = backCameras.length > 0 ? backCameras : videoDevices;

        const mapped: CameraDevice[] = targetList.map((d, i) =>
          classifyCameraDevice(d, i, targetList.length)
        );
        setCameras(mapped);

        const activeTrack = getActiveTrack(videoRef.current);
        const currentSettings = activeTrack?.getSettings?.();
        const currentDeviceId = currentSettings?.deviceId;
        if (currentDeviceId && mapped.some((c) => c.deviceId === currentDeviceId)) {
          preferredDeviceIdRef.current = preferredDeviceIdRef.current || currentDeviceId;
          setSelectedCameraId((prev) => prev || currentDeviceId);
        } else if (mapped.length > 0 && !preferredDeviceIdRef.current) {
          setSelectedCameraId((prev) => (prev && mapped.some((c) => c.deviceId === prev) ? prev : mapped[0].deviceId));
        }
      }
    } catch (err) {
      console.warn("Failed to enumerate camera devices:", err);
    }
  }, [videoRef]);

  useEffect(() => {
    if (!opts.enabled || !videoRef.current) {
      setHasTorch(false);
      setIsTorchOn(false);
      setIsLowLight(false);
      setHasZoom(false);
      lightDetectorRef.current.reset();
      return;
    }

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, SUPPORTED_FORMATS);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);
    let cancelled = false;
    let controls: IScannerControls | null = null;
    let detectRaf = 0;
    const cropCanvas = document.createElement("canvas");
    const nativeDetector = createNativeBarcodeDetector();

    const confirmText = (raw: string) => {
      const normalized = normalizeDetectedValue(raw);
      if (!normalized) return;
      const outcome = stabilizerRef.current.observe(normalized, Date.now());
      if (outcome === "CONFIRMED") {
        onDetectedRef.current(normalized);
      }
    };

    const sampleCanvas = document.createElement("canvas");
    sampleCanvas.width = 32;
    sampleCanvas.height = 32;

    const lightInterval = setInterval(() => {
      if (cancelled || !videoRef.current) return;
      const brightness = sampleVideoBrightness(videoRef.current, sampleCanvas);
      if (brightness !== null) {
        const low = lightDetectorRef.current.update(brightness);
        setIsLowLight(low);
      }
    }, 500);

    // Prefer facingMode on first start. Switching to deviceId:{exact} after
    // enumerateDevices restarts the stream and often leaves a black preview
    // in Telegram iOS WebView until the user taps the camera button.
    const deviceId = preferredDeviceIdRef.current;
    const videoConstraints: MediaTrackConstraints = deviceId
      ? { deviceId: { exact: deviceId } }
      : { facingMode: { ideal: "environment" } };

    reader
      .decodeFromConstraints({ video: videoConstraints }, videoRef.current, (result) => {
        if (cancelled || !result) return;
        confirmText(result.getText());
      })
      .then((c) => {
        if (cancelled) {
          c.stop();
        } else {
          controls = c;
          controlsRef.current = c;
          void videoRef.current?.play?.().catch(() => {
            // Autoplay can be blocked until a tap; the start-camera overlay handles that.
          });

          // Telegram iOS WKWebView often never delivers ZXing callback hits.
          // Native BarcodeDetector (Safari 16.4+) reads the same video element.
          let detectBusy = false;
          const tick = () => {
            if (cancelled) return;
            detectRaf = requestAnimationFrame(tick);
            const video = videoRef.current;
            if (!video || detectBusy || video.readyState < 2 || video.videoWidth < 8) return;
            detectBusy = true;
            const source = cropVideoToViewfinder(video, cropCanvas);
            void detectFromNative(source, nativeDetector)
              .then((values) => {
                if (values.length === 0) {
                  try {
                    const fallback = reader.decodeFromCanvas(source);
                    if (fallback) values.push(fallback.getText());
                  } catch {
                    // NotFoundException every empty frame is expected
                  }
                }
                for (const value of values) confirmText(value);
              })
              .finally(() => {
                detectBusy = false;
              });
          };
          detectRaf = requestAnimationFrame(tick);

          const available = checkTorchAvailability(c, videoRef.current);
          setHasTorch(available);

          // Detect zoom capabilities from active stream track
          const track = getActiveTrack(videoRef.current);
          if (track) {
            const caps = getZoomCapabilities(track);
            if (caps) {
              setMinZoom(caps.min);
              setMaxZoom(caps.max);
              setStepZoom(caps.step);
              setHasZoom(true);
              const currentZ = getTrackZoom(track) ?? caps.min;
              setZoomState(currentZ);
            }
          }

          // Enumerate cameras
          refreshCameras();
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : "Camera access failed";
          let type: CameraErrorType = "unknown";
          if (e instanceof DOMException) {
            if (e.name === "NotAllowedError") {
              type = "permission_denied";
            } else if (e.name === "NotFoundError") {
              type = "not_found";
            }
          } else if (e instanceof Error && e.name === "NotAllowedError") {
            type = "permission_denied";
          } else if (e instanceof Error && e.name === "NotFoundError") {
            type = "not_found";
          }
          setError({ message, type });
          setHasTorch(false);
          setIsTorchOn(false);
          setIsLowLight(false);
          setHasZoom(false);
        }
      });

    return () => {
      cancelled = true;
      cancelAnimationFrame(detectRaf);
      clearInterval(lightInterval);
      controlsRef.current = null;
      lightDetectorRef.current.reset();
      setHasTorch(false);
      setIsTorchOn(false);
      setIsLowLight(false);
      controls?.stop();
    };
  }, [opts.enabled, cameraGeneration, videoRef, refreshCameras]);

  // Listen to device changes if supported
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.addEventListener) return;
    const handleDeviceChange = () => {
      refreshCameras();
    };
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, [refreshCameras]);

  const setTorch = useCallback(
    async (enabled: boolean) => {
      try {
        await setTorchState(enabled, controlsRef.current, videoRef.current);
        setIsTorchOn(enabled);
        if (enabled) {
          lightDetectorRef.current.reset();
          setIsLowLight(false);
        }
      } catch (e) {
        // Safe degradation: do not throw to caller
        console.warn("Failed to set torch state:", e);
      }
    },
    [videoRef]
  );

  const toggleTorch = useCallback(async () => {
    await setTorch(!isTorchOn);
  }, [isTorchOn, setTorch]);

  const enableTorch = useCallback(async () => {
    await setTorch(true);
  }, [setTorch]);

  const setZoom = useCallback(
    async (targetZoom: number) => {
      try {
        const clamped = Math.min(Math.max(targetZoom, minZoom), maxZoom);
        setZoomState(clamped);
        const track = getActiveTrack(videoRef.current);
        if (track) {
          await applyTrackZoom(track, clamped);
        }
      } catch (e) {
        console.warn("Failed to set camera zoom:", e);
      }
    },
    [minZoom, maxZoom, videoRef]
  );

  const resetZoom = useCallback(
    async (preset: number = 1) => {
      await setZoom(preset);
    },
    [setZoom]
  );

  const cycleCamera = useCallback(async () => {
    if (cameras.length <= 1) return;
    const currentIndex = cameras.findIndex((c) => c.deviceId === selectedCameraId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];
    preferredDeviceIdRef.current = nextCamera.deviceId;
    setSelectedCameraId(nextCamera.deviceId);
    setCameraGeneration((generation) => generation + 1);
  }, [cameras, selectedCameraId]);

  const restartCamera = useCallback(() => {
    setError(null);
    setCameraGeneration((generation) => generation + 1);
  }, []);

  const switchCamera = useCallback(
    async (deviceId?: string) => {
      if (deviceId) {
        preferredDeviceIdRef.current = deviceId;
        setSelectedCameraId(deviceId);
        setCameraGeneration((generation) => generation + 1);
      } else {
        await cycleCamera();
      }
    },
    [cycleCamera]
  );

  const currentCamera = cameras.find((c) => c.deviceId === selectedCameraId) || cameras[0];
  const currentCameraLabel = currentCamera?.label || "Камера";
  const hasMultipleCameras = cameras.length > 1;

  return {
    error,
    hasTorch,
    isTorchOn,
    isLowLight,
    toggleTorch,
    enableTorch,
    zoom,
    minZoom,
    maxZoom,
    stepZoom,
    hasZoom,
    setZoom,
    resetZoom,
    cameras,
    selectedCameraId,
    currentCameraLabel,
    hasMultipleCameras,
    switchCamera,
    cycleCamera,
    restartCamera,
  };
}
